import React from 'react';
import { X, Plus, Minus, ShoppingBag } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';

const CartDrawer = () => {
  const { 
    cartItems, 
    isCartOpen, 
    setIsCartOpen, 
    removeFromCart, 
    updateQuantity, 
    cartTotal 
  } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate('/checkout');
  };

  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-coconut flex flex-col">
        <SheetHeader className="border-b border-stone-200 pb-4">
          <SheetTitle className="font-display text-xl flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" />
            Your Cart ({cartItems.length})
          </SheetTitle>
        </SheetHeader>

        {cartItems.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
            <ShoppingBag className="h-16 w-16 text-stone-300 mb-4" />
            <h3 className="text-text-primary font-medium text-lg mb-2">Your cart is empty</h3>
            <p className="text-text-subtle text-sm mb-6">Add items to get started</p>
            <Button 
              onClick={() => { setIsCartOpen(false); navigate('/shop'); }}
              className="bg-accent-ui hover:bg-accent-ui/90 text-white"
            >
              Continue Shopping
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto py-4 space-y-4">
              {cartItems.map((item) => (
                <div key={item.cartItemId} className="flex gap-4 p-3 bg-white rounded-lg">
                  <div className="w-20 h-24 bg-stone-100 rounded overflow-hidden flex-shrink-0">
                    <img 
                      src={item.image} 
                      alt={item.name} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-text-primary font-medium text-sm truncate">{item.name}</h4>
                    <p className="text-text-subtle text-xs mt-1">
                      {item.selectedSize} / {item.selectedColor}
                    </p>
                    <p className="text-text-primary font-semibold text-sm mt-2">
                      ${item.price.toFixed(2)}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                        className="p-1 rounded-full hover:bg-stone-100 transition-colors"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                        className="p-1 rounded-full hover:bg-stone-100 transition-colors"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.cartItemId)}
                    className="text-text-subtle hover:text-text-primary transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="border-t border-stone-200 pt-4 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-text-subtle">Subtotal</span>
                <span className="text-text-primary font-semibold text-lg">
                  ${cartTotal.toFixed(2)}
                </span>
              </div>
              <p className="text-text-subtle text-xs">Shipping calculated at checkout</p>
              <Button 
                onClick={handleCheckout}
                className="w-full bg-accent-ui hover:bg-accent-ui/90 text-white h-12 text-base"
              >
                Checkout
              </Button>
              <Button 
                variant="outline"
                onClick={() => { setIsCartOpen(false); navigate('/shop'); }}
                className="w-full border-stone-300"
              >
                Continue Shopping
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
